import { useEffect, useState } from 'react';

const NETWORK_CHECK_PATH = '/__network-check__';
const isBrowser = typeof window !== 'undefined';

const getBrowserOnlineStatus = () => {
  if (!isBrowser) {
    return true;
  }

  return window.navigator.onLine;
};

const getNetworkCheckUrl = () =>
  `${NETWORK_CHECK_PATH}?network-check=${Date.now()}&pathname=${encodeURIComponent(window.location.pathname)}`;

const createRequestTimeoutController = () => {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => {
    controller.abort();
  }, 4000);

  return { controller, timeoutId };
};

export const verifyNetworkConnection = async () => {
  if (!isBrowser) {
    return true;
  }

  if (!getBrowserOnlineStatus()) {
    return false;
  }

  const { controller, timeoutId } = createRequestTimeoutController();

  try {
    await fetch(getNetworkCheckUrl(), {
      method: 'GET',
      cache: 'no-store',
      credentials: 'same-origin',
      signal: controller.signal,
    });

    return true;
  } catch {
    return false;
  } finally {
    window.clearTimeout(timeoutId);
  }
};

const useNetworkStatus = () => {
  const [isOnline, setIsOnline] = useState(getBrowserOnlineStatus);

  useEffect(() => {
    let isMounted = true;

    const syncOnlineStatus = async () => {
      if (document.visibilityState === 'hidden') {
        return;
      }

      const onlineStatus = await verifyNetworkConnection();

      if (isMounted) {
        setIsOnline(onlineStatus);
      }
    };

    syncOnlineStatus();

    window.addEventListener('online', syncOnlineStatus);
    window.addEventListener('offline', syncOnlineStatus);
    window.addEventListener('focus', syncOnlineStatus);
    document.addEventListener('visibilitychange', syncOnlineStatus);
    const intervalId = window.setInterval(syncOnlineStatus, 15000);

    return () => {
      isMounted = false;
      window.removeEventListener('online', syncOnlineStatus);
      window.removeEventListener('offline', syncOnlineStatus);
      window.removeEventListener('focus', syncOnlineStatus);
      document.removeEventListener('visibilitychange', syncOnlineStatus);
      window.clearInterval(intervalId);
    };
  }, []);

  return isOnline;
};

export default useNetworkStatus;
